<template>
    <Popover @open="handleOpen" @close="handleClose">
        <template #trigger>
            <span class="license-popover-default-trigger">
                <slot name="trigger">{{ displayLicenseNumber }}</slot>
            </span>
        </template>
        <template #content="{ close }">
            <div class="license-popover-content" v-if="setCloseFunction(close)">
                <slot
                    :close="close"
                    :licenseNumber="displayLicenseNumber"
                    :carId="carId"
                    :carUuid="carUuid"
                >
                    <div class="license-info">
                        <div class="license-name-row">
                            <div class="license-name">{{ displayLicenseNumber }}</div>
                        </div>
                        <div v-if="secondaryInfo" class="license-id">{{ secondaryInfo }}</div>
                    </div>
                </slot>

                <div class="license-popover-actions">
                    <Button
                        v-if="showRevenueButton"
                        v-tooltip="getRevenueTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageRevenue && isCarFiltered"
                        :class="{ 'current-page': isCurrentPageRevenue && isCarFiltered }"
                        @onClick="handleNavigate('revenue', close)"
                    >
                        <i class="ri-money-dollar-circle-line"></i>
                        <span class="button-hotkey">U</span>
                    </Button>

                    <Button
                        v-if="showShiftsButton"
                        v-tooltip="getShiftsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageShifts && isCarFiltered"
                        :class="{ 'current-page': isCurrentPageShifts && isCarFiltered }"
                        @onClick="handleNavigate('shifts', close)"
                    >
                        <i class="ri-repeat-line"></i>
                        <span class="button-hotkey">S</span>
                    </Button>

                    <Button
                        v-if="showTripsButton"
                        v-tooltip="getTripsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageTrips && isCarFiltered"
                        :class="{ 'current-page': isCurrentPageTrips && isCarFiltered }"
                        @onClick="handleNavigate('trips', close)"
                    >
                        <i class="ri-steering-2-line"></i>
                        <span class="button-hotkey">E</span>
                    </Button>

                    <Button
                        v-if="showOperationRideButton"
                        v-tooltip="getOperationRideTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageOperationRide && isCarFiltered"
                        :class="{
                            'current-page': isCurrentPageOperationRide && isCarFiltered,
                        }"
                        @onClick="handleNavigate('operationride', close)"
                    >
                        <i class="ri-route-line"></i>
                        <span class="button-hotkey">B</span>
                    </Button>

                    <Button
                        v-if="showCarsButton"
                        v-tooltip="getCarsTooltip()"
                        variant="icon"
                        size="small"
                        :disabled="isCurrentPageCars && isCarFiltered"
                        :class="{ 'current-page': isCurrentPageCars && isCarFiltered }"
                        @onClick="handleNavigate('cars', close)"
                    >
                        <i class="ri-car-line"></i>
                        <span class="button-hotkey">F</span>
                    </Button>
                </div>

                <div v-if="$slots.actions" class="license-popover-custom-actions">
                    <slot
                        name="actions"
                        :close="close"
                        :licenseNumber="displayLicenseNumber"
                        :carId="carId"
                        :carUuid="carUuid"
                    />
                </div>
            </div>
        </template>
    </Popover>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from '@/components/widgets/Button.vue';
import Popover from '@/components/widgets/Popover.vue';

export default {
    name: 'LicenseNumberPopover',
    components: { Button, Popover },
    props: {
        licenseNumber: {
            type: [String, Number],
            required: true,
            default: '',
        },
        carId: {
            required: false,
            default: undefined,
            validator: (value) =>
                value === undefined || typeof value === 'string' || typeof value === 'number',
        },
        carUuid: { type: [String, Number], default: null },
        concession: { type: [String, Number], default: null },
        trigger: {
            type: String,
            default: 'click',
            validator: (v) => ['click'].includes(v),
        },
    },
    data() {
        return {
            isOpen: false,
            closeFunction: null,
        };
    },
    computed: {
        ...mapGetters(['isPlanBasic']),

        displayLicenseNumber() {
            return String(this.licenseNumber);
        },

        secondaryInfo() {
            if (this.concession) {
                return `Konzession: ${this.concession}`;
            }
            return ``;
        },

        currentRoute() {
            return this.$route.path.split('/')[1];
        },

        showShiftsButton() {
            return this.isPlanBasic;
        },

        showTripsButton() {
            return this.isPlanBasic;
        },

        showRevenueButton() {
            return this.isPlanBasic;
        },

        showOperationRideButton() {
            return this.isPlanBasic;
        },

        showCarsButton() {
            return this.isPlanBasic;
        },

        isCurrentPageShifts() {
            return this.currentRoute === 'shifts';
        },

        isCurrentPageTrips() {
            return this.currentRoute === 'trips';
        },

        isCurrentPageRevenue() {
            return this.currentRoute === 'revenue';
        },

        isCurrentPageOperationRide() {
            return this.currentRoute === 'operationride';
        },

        isCurrentPageCars() {
            return this.currentRoute === 'cars';
        },

        canFilterCurrentPage() {
            const filterablePages = ['shifts', 'trips', 'revenue', 'operationride', 'cars'];
            return filterablePages.includes(this.currentRoute);
        },

        isCarFiltered() {
            const currentCar = this.$route.query.car;
            return currentCar === String(this.licenseNumber);
        },

        availableHotkeys() {
            const hotkeys = [];

            if (this.showShiftsButton) {
                hotkeys.push({ key: 'S', label: 'Schichten' });
            }

            if (this.showTripsButton) {
                hotkeys.push({ key: 'E', label: 'Einzelfahrten' });
            }

            if (this.showRevenueButton) {
                hotkeys.push({ key: 'U', label: 'Umsatz' });
            }

            if (this.showOperationRideButton) {
                hotkeys.push({ key: 'B', label: 'Betriebsnachweis' });
            }

            if (this.showCarsButton) {
                hotkeys.push({ key: 'F', label: 'Fahrzeuge' });
            }

            return hotkeys;
        },
    },
    methods: {
        handleOpen() {
            this.isOpen = true;
            document.addEventListener('keydown', this.handleKeydown);

            this.$emit('open', {
                licenseNumber: this.displayLicenseNumber,
                carId: this.carId,
                carUuid: this.carUuid,
            });
        },
        handleClose() {
            this.isOpen = false;
            this.closeFunction = null;
            document.removeEventListener('keydown', this.handleKeydown);

            this.$emit('close', {
                licenseNumber: this.displayLicenseNumber,
                carId: this.carId,
                carUuid: this.carUuid,
            });
        },
        setCloseFunction(close) {
            this.closeFunction = close;
            return true;
        },
        handleKeydown(e) {
            if (!this.isOpen || !this.closeFunction) return;

            switch (e.key.toLowerCase()) {
                case 's':
                    if (this.showShiftsButton) {
                        e.preventDefault();
                        this.handleNavigate('shifts', this.closeFunction);
                    }
                    break;
                case 'e':
                    if (this.showTripsButton) {
                        e.preventDefault();
                        this.handleNavigate('trips', this.closeFunction);
                    }
                    break;
                case 'u':
                    if (this.showRevenueButton) {
                        e.preventDefault();
                        this.handleNavigate('revenue', this.closeFunction);
                    }
                    break;
                case 'b':
                    if (this.showOperationRideButton) {
                        e.preventDefault();
                        this.handleNavigate('operationride', this.closeFunction);
                    }
                    break;
                case 'f':
                    if (this.showCarsButton) {
                        e.preventDefault();
                        this.handleNavigate('cars', this.closeFunction);
                    }
                    break;
            }
        },
        handleNavigate(page, close) {
            if (this.isCurrentPage(page) && !this.isCarFiltered) {
                this.handleFilterCurrentPage(close);
                return;
            }

            const query = {
                car: this.licenseNumber,
            };

            const currentQuery = this.$route.query;
            if (currentQuery.from) query.from = currentQuery.from;
            if (currentQuery.to) query.to = currentQuery.to;
            if (currentQuery.concession) query.concession = currentQuery.concession;
            if (currentQuery.driver) query.driver = currentQuery.driver;

            this.$router.push({ path: `/${page}`, query });
            close();
        },
        handleFilterCurrentPage(close) {
            this.$emit('filter', {
                carId: this.carId,
                licenseNumber: this.displayLicenseNumber,
            });
            close();
        },

        getRevenueTooltip() {
            if (this.isCurrentPageRevenue) {
                return this.isCarFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrzeug filtern (U)';
            }
            return 'Im Umsatz Anzeigen (U)';
        },
        getShiftsTooltip() {
            if (this.isCurrentPageShifts) {
                return this.isCarFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrzeug filtern (S)';
            }
            return 'In Schichten Anzeigen (S)';
        },
        getTripsTooltip() {
            if (this.isCurrentPageTrips) {
                return this.isCarFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrzeug filtern (E)';
            }
            return 'In Einzelfahrten Anzeigen (E)';
        },
        getOperationRideTooltip() {
            if (this.isCurrentPageOperationRide) {
                return this.isCarFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrzeug filtern (B)';
            }
            return 'In Betriebsnachweis Anzeigen (B)';
        },
        getCarsTooltip() {
            if (this.isCurrentPageCars) {
                return this.isCarFiltered ? 'Aktuelle Seite' : 'Nach diesem Fahrzeug filtern (F)';
            }
            return 'In Fahrzeuge Anzeigen (F)';
        },
        isCurrentPage(page) {
            return this.currentRoute === page;
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
};
</script>

<style lang="scss" scoped>
.license-popover-default-trigger {
    font-weight: 500;
    color: var(--color-blue-dark);
    padding: 2px 4px;
    border-radius: 3px;
    border: 0.5px solid rgba(167, 167, 167, 0.3);
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: rgba(167, 167, 167, 0.2);

    &:hover {
        background-color: rgba(167, 167, 167, 0.4);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
}
.license-popover-content {
    display: flex;
    flex-direction: column;
}
.license-info {
    margin-bottom: 4px;
    .license-name-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 1px;
    }
    .license-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--color-text-black);
        flex: 1;
    }
    .license-id {
        font-size: 0.75rem;
        color: var(--color-gray);
    }
}
.license-popover-actions {
    margin-top: 6px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;

    .Button {
        i {
            width: 1em;
            text-align: center;
        }
    }
}
.license-popover-custom-actions {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 0.5px solid rgba(0, 0, 0, 0.08);
}
.button-hotkey {
    font-weight: 600;
    color: var(--color-text-black);
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
    margin-left: 4px;
    font-size: 1rem;
    font-family: 'Courier New', monospace;
}

.current-page {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
        opacity: 0.5;
    }

    .button-hotkey {
        opacity: 0.5;
    }
}
</style>
